import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { BLOG_POSTS } from "../blogData";

// Custom SVG User Icon for metadata
const UserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4 text-[#e21b1b]"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Custom SVG Tag Icon for metadata
const TagIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4 text-[#e21b1b]"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" strokeLinecap="round" strokeWidth="3" />
  </svg>
);

// Custom SVG Cloche Lid Icon for badge
const ClocheIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4 text-[#f25e1c]"
  >
    <path d="M12 4v2" />
    <circle cx="12" cy="4" r="0.5" fill="currentColor" />
    <path d="M4 14c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    <path d="M2 15h20v2H2z" />
  </svg>
);

// Custom Burger doodle (background left)
const BurgerDoodle = () => (
  <svg
    viewBox="0 0 100 100"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
    className="w-48 h-48 text-[#1a0e05]/[0.04] pointer-events-none select-none"
  >
    <path d="M20 50 C20 22, 80 22, 80 50 Z" />
    <path d="M15 54 Q20 50 25 54 Q30 58 35 54 Q40 50 45 54 Q50 58 55 54 Q60 50 65 54 Q70 58 75 54 Q80 50 85 54" />
    <path d="M18 60 L82 60 L78 65 L22 65 Z" />
    <rect x="20" y="68" width="60" height="9" rx="3" />
    <path d="M22 81 C22 86, 78 86, 78 81 Z" />
    <line x1="22" y1="81" x2="78" y2="81" />
  </svg>
);

// Custom Pizza doodle (background right)
const PizzaDoodle = () => (
  <svg
    viewBox="0 0 100 100"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
    className="w-44 h-44 text-[#1a0e05]/[0.04] pointer-events-none select-none"
  >
    <path d="M20 20 L80 32 L46 84 Z" />
    <path d="M20 20 C35 22, 65 26, 80 32" strokeWidth="2" />
    <circle cx="42" cy="40" r="5" />
    <circle cx="58" cy="48" r="6" />
    <circle cx="45" cy="62" r="4.5" />
    <path d="M30 35 Q38 38 40 44" />
    <path d="M62 40 Q54 44 56 50" />
  </svg>
);

const LatestNews = () => {
  const scrollContainerRef = useRef(null);

  const newsItems = BLOG_POSTS.slice(0, 6);

  // Carousel scroll helpers
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -380,
        behavior: "smooth"
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 380,
        behavior: "smooth"
      });
    }
  };

  return (
    <section className="relative w-full bg-[#faf8f5] py-20 px-6 overflow-hidden select-none">
      
      {/* Background Line-Art Doodles */}
      <div className="absolute left-4 bottom-6 z-0 opacity-40 pointer-events-none hidden md:block">
        <BurgerDoodle />
      </div>
      <div className="absolute right-4 top-8 z-0 opacity-40 pointer-events-none hidden md:block">
        <PizzaDoodle />
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Section Header */}
        <div className="flex flex-col items-center text-center mb-12">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-50 px-3 py-1 rounded-full border border-orange-100/80 mb-3 shadow-xs">
            <ClocheIcon />
            <span className="text-[#f25e1c] font-black tracking-widest text-[10px] uppercase font-mono">
              LATEST NEWS
            </span>
            <ClocheIcon />
          </div>
          {/* Title */}
          <h2 className="text-3xl md:text-[40px] font-extrabold text-[#071520] tracking-tight leading-tight font-sans">
            Our Latest Foods News
          </h2>
        </div>

        {/* Slider Section Wrapper */}
        <div className="relative w-full flex items-center">
          
          {/* Left Arrow Button (transparent circle thin red outline) */}
          <button
            onClick={scrollLeft}
            aria-label="Previous posts"
            className="absolute left-[-20px] lg:left-[-40px] z-20 bg-transparent border border-[#e21b1b] hover:bg-[#e21b1b]/5 active:scale-95 text-[#e21b1b] w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm focus:outline-none hidden sm:flex"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="w-4 h-4"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="w-full flex gap-6 overflow-x-auto scrollbar-none snap-x snap-mandatory py-4 px-1"
            style={{ scrollSnapType: "x mandatory" }}
          >
            {newsItems.map((item) => (
              <div
                key={item.id}
                className="snap-start flex-shrink-0 w-full sm:w-[320px] md:w-[360px] bg-white rounded-xs shadow-[0_8px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)] transition-all duration-300 overflow-hidden border border-neutral-100 flex flex-col group"
              >
                
                {/* News Image Frame */}
                <div className="relative h-52 w-full overflow-hidden">
                  <img
                    src={item.imgUrl}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                    loading="lazy"
                  />
                </div>

                {/* Content Block */}
                <div className="p-6 flex flex-col flex-1">
                  
                  {/* Date & Meta Row */}
                  <div className="flex items-center gap-4 mb-4">
                    {/* Red Date Block */}
                    <div className="bg-[#e21b1b] text-white flex flex-col items-center justify-center w-12 h-12 rounded-sm font-sans shrink-0 shadow-sm leading-none">
                      <span className="text-base font-black tracking-tighter">{item.day}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5">{item.month}</span>
                    </div>

                    {/* Metadata Items */}
                    <div className="flex items-center gap-4">
                      {/* Author */}
                      <span className="flex items-center gap-1.5 text-xs text-neutral-500 font-bold font-sans">
                        <UserIcon />
                        By {item.author}
                      </span>
                      {/* Tag */}
                      <span className="flex items-center gap-1.5 text-xs text-neutral-500 font-bold font-sans">
                        <TagIcon />
                        {item.tag}
                      </span>
                    </div>
                  </div>

                  {/* News Title */}
                  <h3 className="text-[17px] md:text-[19px] font-extrabold text-[#071520] group-hover:text-[#e21b1b] transition-colors duration-200 font-sans leading-snug cursor-pointer line-clamp-2">
                    <Link to={`/blog/${item.slug}`}>{item.title}</Link>
                  </h3>

                  {/* Read More Link */}
                  <div className="mt-auto pt-6">
                    <Link
                      to={`/blog/${item.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#071520] hover:text-[#e21b1b] transition-colors duration-200"
                    >
                      Read More
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="w-3 h-3 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                      >
                        <line x1="7" y1="17" x2="17" y2="7" />
                        <polyline points="7 7 17 7 17 17" />
                      </svg>
                    </Link>
                  </div>

                </div>

              </div>
            ))}
          </div>

          {/* Right Arrow Button (solid red circle white arrow) */}
          <button
            onClick={scrollRight}
            aria-label="Next posts"
            className="absolute right-[-20px] lg:right-[-40px] z-20 bg-[#e21b1b] hover:bg-[#c81616] active:scale-95 text-white w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 shadow-md focus:outline-none hidden sm:flex"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="w-4 h-4"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>

        </div>

      </div>

    </section>
  );
};

export default LatestNews;
